import { useQueryClient } from '@tanstack/react-query';
import {
  MutableRefObject,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PAYMENT_SINGLE_MONTH_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { EditMarketplacePaymentDialog } from 'src/dialogs/EditMarketplacePaymentDialog/EditMarketplacePaymentDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  MarketplacePaymentClient,
  MarketplacePaymentInput,
  MarketplacePaymentState,
  MarketplaceSalePayment,
} from 'src/WebApiController';

export interface EditMarketplacePaymentForm {
  externalPaymentId: string;
  paymentReferenceId?: string;
  paymentDate: string;
  receivedDate?: string;
  paymentState: MarketplacePaymentState;
  currency: string;
}

export interface EditMarketplacePaymentRefActions {
  showEditMarketplacePaymentDialog: () => void;
}

interface EditMarketplacePaymentProps {
  marketplaceSalePayment: MarketplaceSalePayment;
  editMarketplacePaymentActionsRef?: MutableRefObject<EditMarketplacePaymentRefActions | null>;
}

export const EditMarketplacePayment = ({
  editMarketplacePaymentActionsRef,
  marketplaceSalePayment,
}: EditMarketplacePaymentProps) => {
  const queryClient = useQueryClient();
  const { activeAccountWebClientConfig } = useAppContext();
  const editMarketplacePaymentDialog = useBasicDialog();
  const { showErrorDialog } = useClientLogContext();

  const onCancel = useCallback(() => {
    editMarketplacePaymentDialog.closeDialog();
  }, [editMarketplacePaymentDialog]);

  const onEdit = useCallback(
    async (updatedPayment: MarketplacePaymentInput) => {
      await tryInvokeApi(
        async () => {
          const success = await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).updateMarketplacePayment(updatedPayment);

          if (success) {
            const isExpanded = true;
            queryClient.invalidateQueries({
              queryKey: [PAYMENT_SINGLE_MONTH_QUERY_KEY, isExpanded],
              refetchType: 'active',
            });
            editMarketplacePaymentDialog.closeDialog();
          }
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.updateMarketplacePayment',
            error as Error
          );
        },
        () => {}
      );
    },
    [
      activeAccountWebClientConfig,
      editMarketplacePaymentDialog,
      queryClient,
      showErrorDialog,
    ]
  );

  useImperativeHandle(
    editMarketplacePaymentActionsRef,
    () => ({
      showEditMarketplacePaymentDialog: () =>
        editMarketplacePaymentDialog.launchDialog(),
    }),
    [editMarketplacePaymentDialog]
  );

  return (
    <>
      {editMarketplacePaymentDialog.dialogProps.isOpen && (
        <EditMarketplacePaymentDialog
          {...editMarketplacePaymentDialog.dialogProps}
          onSubmit={onEdit}
          onCancel={onCancel}
          marketplacePaymentData={marketplaceSalePayment}
        />
      )}
    </>
  );
};
